import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, ShieldCheck, DollarSign, Headphones, MapPin, Quote as QuoteIcon, Award, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { Reveal, MaskLine } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SERVICES, COMING_SOON_SERVICES, SOLUTIONS, LOCATIONS, DIFFERENTIATORS, TESTIMONIALS, IMG, HERO_IMAGES } from "@/data/site";

const Hero = () => {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#0B192C]" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <AnimatePresence>
          <motion.img key={idx} src={HERO_IMAGES[idx]} alt="Cross-border freight truck on the highway"
            initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover" />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/70 to-[#0B192C]/30" />
      <div className="absolute inset-0 noise opacity-[0.06]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full pb-16 lg:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-6">
          <span className="h-[2px] w-10 bg-[#FF5A00]" />
          <span className="font-head uppercase tracking-[0.3em] text-[#FF5A00] text-sm font-700">Ontario · Canada–US Freight Brokerage</span>
        </motion.div>

        <h1 className="font-head font-black uppercase text-white text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.85] tracking-tighter max-w-5xl">
          <MaskLine delay={0.1}>Freight that</MaskLine>
          <MaskLine delay={0.22}><span className="text-[#FF5A00]">crosses borders</span></MaskLine>
          <MaskLine delay={0.34}>without friction</MaskLine>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }}
          className="text-[#CBD5E1] text-lg md:text-xl max-w-xl mt-8 leading-relaxed">
          Your dedicated Canada–US freight partner — full truckload, LTL, and specialized oversize & heavy-haul. Direct broker access, transparent rates, no call centers.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
          className="flex flex-wrap items-center gap-4 mt-10">
          <Link to="/quote" data-testid="hero-quote-btn" className="group bg-[#FF5A00] text-white font-head font-700 uppercase tracking-[0.15em] px-8 py-4 flex items-center gap-3 hover:bg-[#E65100] transition-colors">
            Get a Free Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/services" data-testid="hero-services-btn" className="border-2 border-white/40 text-white font-head font-700 uppercase tracking-[0.15em] px-8 py-4 hover:bg-white hover:text-[#0B192C] transition-colors">
            Explore Services
          </Link>
        </motion.div>

        <div className="flex gap-2 mt-12">
          {HERO_IMAGES.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`}
              className={`h-1 transition-all duration-500 ${i === idx ? "w-12 bg-[#FF5A00]" : "w-6 bg-white/30"}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatBar = () => (
  <div className="bg-[#0B192C] text-white border-t-4 border-[#FF5A00]">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
      {[["7+", "Years industry experience"], ["CIFFA", "Certified freight forwarder"], ["24/7", "Direct broker access"], ["0", "Hidden fees, ever"]].map(([n, l], i) => (
        <Reveal key={i} delay={i * 0.08} className="py-10 px-4 md:px-8">
          <div className="font-head font-black text-4xl md:text-5xl text-[#FF5A00]">{n}</div>
          <div className="text-[#94A3B8] text-sm mt-2 uppercase tracking-wide font-600">{l}</div>
        </Reveal>
      ))}
    </div>
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="h-[2px] w-8 bg-[#FF5A00]" />
    <span className="font-head uppercase tracking-[0.3em] text-[#FF5A00] text-sm font-700">{children}</span>
  </div>
);

const Services = () => (
  <section className="py-24 lg:py-32 bg-[#F8FAFC]" id="services" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal>
        <SectionLabel>What We Move</SectionLabel>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <h2 className="font-head font-black uppercase text-5xl md:text-7xl leading-[0.9] tracking-tighter max-w-2xl">Services built for the border</h2>
          <p className="text-[#475569] text-lg max-w-md">Every mode you need to keep freight moving between Canada and the US — with oversize & heavy-haul as our specialty.</p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 3) * 0.08}>
            <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
              className="group block bg-white border-2 border-[#0B192C] h-full hover:-translate-y-2 transition-transform duration-300 hover:hard-shadow-orange">
              <div className="relative h-44 overflow-hidden border-b-2 border-[#0B192C]">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" />
                <div className="absolute top-0 left-0 bg-[#0B192C] text-white p-3"><s.icon size={22} className="text-[#FF5A00]" /></div>
              </div>
              <div className="p-7">
                <div className="flex items-center justify-between">
                  <h3 className="font-head font-800 uppercase text-2xl tracking-tight">{s.title}</h3>
                  <ArrowUpRight className="text-[#FF5A00] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-[#475569] mt-3 leading-relaxed">{s.short}</p>
              </div>
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.16}>
          <div className="bg-[#0B192C] text-white h-full p-7 flex flex-col" data-testid="coming-soon-card">
            <div className="font-head uppercase tracking-[0.25em] text-[#FF5A00] text-xs font-700 mb-6">Coming Soon</div>
            <div className="space-y-5 flex-1">
              {COMING_SOON_SERVICES.map((c) => (
                <div key={c.title} className="flex items-start gap-4 pb-5 border-b border-white/10 last:border-0">
                  <c.icon size={22} className="text-[#94A3B8] mt-1" />
                  <div>
                    <div className="font-head font-700 uppercase tracking-wide text-lg text-[#CBD5E1]">{c.title}</div>
                    <div className="text-[#64748b] text-sm">{c.note}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[#64748b] text-xs mt-6">Ocean, air & customs brokerage activate as we grow — no site rebuild required.</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Solutions = () => (
  <section className="py-24 lg:py-32 bg-white border-y-2 border-[#0B192C]/5" data-testid="solutions-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal><SectionLabel>Who We Serve</SectionLabel>
        <h2 className="font-head font-black uppercase text-5xl md:text-6xl tracking-tighter mb-14 max-w-2xl">Solutions by industry</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.title} delay={(i % 2) * 0.08}>
            <div className="group flex items-start gap-6 p-8 border-2 border-[#0B192C]/10 hover:border-[#FF5A00] hover:bg-[#F8FAFC] transition-colors" data-testid={`solution-${i}`}>
              <div className="font-head font-black text-5xl text-[#0B192C]/10 group-hover:text-[#FF5A00] transition-colors leading-none">0{i + 1}</div>
              <div>
                <h3 className="font-head font-800 uppercase text-2xl tracking-tight">{s.title}</h3>
                <p className="text-[#475569] mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Locations = () => (
  <section className="py-24 lg:py-32 bg-[#0B192C] text-white relative overflow-hidden" data-testid="locations-section">
    <div className="absolute inset-0 noise opacity-[0.05]" />
    <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <SectionLabel>Coverage</SectionLabel>
          <h2 className="font-head font-black uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">Where we<br />haul freight</h2>
          <p className="text-[#94A3B8] text-lg mt-6 max-w-md">Coast-to-coast Canadian coverage and full door-to-door cross-border service into the United States.</p>
          <div className="mt-8 relative border-2 border-white/10 overflow-hidden">
            <img src={IMG.aerial} alt="Aerial view of freight logistics yard" className="w-full h-56 object-cover opacity-70" />
          </div>
        </Reveal>
        <div className="space-y-4">
          {LOCATIONS.map((l, i) => (
            <Reveal key={l.title} delay={i * 0.08}>
              <div className={`flex items-center justify-between p-6 border-2 ${l.active ? "border-[#FF5A00] bg-white/[0.03]" : "border-white/10"}`} data-testid={`location-${i}`}>
                <div className="flex items-center gap-4">
                  <MapPin size={22} className={l.active ? "text-[#FF5A00]" : "text-[#64748b]"} />
                  <div>
                    <div className={`font-head font-800 uppercase text-xl tracking-tight ${l.active ? "text-white" : "text-[#94A3B8]"}`}>{l.title}</div>
                    <div className="text-[#64748b] text-sm">{l.desc}</div>
                  </div>
                </div>
                {!l.active && <span className="text-[10px] font-700 uppercase tracking-wider text-[#94A3B8] border border-white/15 px-2 py-1 whitespace-nowrap">Coming Soon</span>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ValueProp = () => {
  const items = [
    { icon: DollarSign, title: "Transparent Pricing", desc: "The rate we quote is what you plan around. No hidden accessorials, no surprise line items." },
    { icon: Headphones, title: "Direct Broker Access", desc: "Talk to the person actually moving your freight — not a rotating call-center queue." },
    { icon: ShieldCheck, title: "Reliability First", desc: "Vetted carriers, proactive updates, and a founder who stakes his name on every load." },
  ];
  return (
    <section className="py-24 lg:py-32 bg-[#F8FAFC]" data-testid="value-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal><SectionLabel>Why Cargowyn</SectionLabel>
          <h2 className="font-head font-black uppercase text-5xl md:text-6xl tracking-tighter mb-14 max-w-3xl">Savings and reliability, without the runaround</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.1}>
              <div className="bg-white border-2 border-[#0B192C] p-8 h-full hover:hard-shadow transition-shadow duration-300">
                <div className="w-14 h-14 bg-[#FF5A00] flex items-center justify-center mb-6"><it.icon className="text-white" size={26} /></div>
                <h3 className="font-head font-800 uppercase text-2xl tracking-tight">{it.title}</h3>
                <p className="text-[#475569] mt-3 leading-relaxed">{it.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CLIENT_LOGOS = [
  { name: "OES", url: "https://customer-assets-eiarnc6j.emergentagent.net/job_broker-canada-us/artifacts/sb794rbb_Screenshot%202026-09-23%20at%206.00.12%E2%80%AFPM.png" },
  { name: "NHL", url: "https://customer-assets-eiarnc6j.emergentagent.net/job_broker-canada-us/artifacts/l40bwb12_Screenshot%202026-09-23%20at%206.00.59%E2%80%AFPM.png" },
  { name: "Scotiabank Arena", url: "https://customer-assets-eiarnc6j.emergentagent.net/job_broker-canada-us/artifacts/xpo2260n_Screenshot%202026-09-23%20at%206.03.56%E2%80%AFPM.png" },
  { name: "Canada Life Centre", url: "https://customer-assets-eiarnc6j.emergentagent.net/job_broker-canada-us/artifacts/9omvxc7e_Screenshot%202026-09-23%20at%206.05.29%E2%80%AFPM.png" },
  { name: "Noramco", url: "https://customer-assets-eiarnc6j.emergentagent.net/job_broker-canada-us/artifacts/61oxmqmu_Screenshot%202026-09-23%20at%206.08.02%E2%80%AFPM.png" },
];

const Logos = () => (
  <section className="py-16 bg-white border-y-2 border-[#0B192C]/5" data-testid="logos-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal><p className="text-center font-head uppercase tracking-[0.3em] text-[#94A3B8] text-sm font-700 mb-10">Companies we work with</p></Reveal>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
        {CLIENT_LOGOS.map((logo, i) => (
          <Reveal key={logo.name} delay={i * 0.06}>
            <div className="h-24 bg-white border border-[#0B192C]/10 rounded-sm flex items-center justify-center p-4 grayscale hover:grayscale-0 transition-all duration-500" data-testid={`client-logo-${i}`}>
              <img src={logo.url} alt={`${logo.name} logo`} loading="lazy" className="max-h-full max-w-full object-contain" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Differentiators = () => (
  <section className="py-24 lg:py-32 bg-[#F8FAFC]" data-testid="different-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal><SectionLabel>Manifesto</SectionLabel>
        <h2 className="font-head font-black uppercase text-5xl md:text-7xl tracking-tighter mb-16 max-w-3xl">What makes us different</h2>
      </Reveal>
      <div className="space-y-0 border-t-2 border-[#0B192C]">
        {DIFFERENTIATORS.map((d, i) => (
          <Reveal key={d.num} delay={i * 0.05}>
            <div className="group grid md:grid-cols-12 gap-6 items-center py-10 border-b-2 border-[#0B192C]/10 hover:bg-white transition-colors px-4" data-testid={`diff-${i}`}>
              <div className="md:col-span-2 font-head font-black text-6xl md:text-7xl text-[#FF5A00] leading-none">{d.num}</div>
              <h3 className="md:col-span-4 font-head font-800 uppercase text-3xl tracking-tight">{d.title}</h3>
              <p className="md:col-span-6 text-[#475569] text-lg leading-relaxed">{d.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => {
  const [i, setI] = useState(0);
  const t = TESTIMONIALS[i];
  return (
    <section className="py-24 lg:py-32 bg-[#0B192C] text-white relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0 noise opacity-[0.05]" />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 relative">
        <SectionLabel>Client Voices</SectionLabel>
        <QuoteIcon size={64} className="text-[#FF5A00] mb-6" />
        <AnimatePresence mode="wait">
          <motion.blockquote key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}
            className="font-head font-700 uppercase text-3xl md:text-5xl leading-[1.05] tracking-tight min-h-[180px]">
            "{t.quote}"
          </motion.blockquote>
        </AnimatePresence>
        <div className="flex items-center justify-between mt-8 flex-wrap gap-4">
          <div>
            <div className="font-head font-800 uppercase text-xl">{t.name}</div>
            <div className="text-[#64748b] text-sm italic">{t.role}</div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setI((p) => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} data-testid="testimonial-prev" className="w-12 h-12 border-2 border-white/20 flex items-center justify-center hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors"><ChevronLeft /></button>
            <button onClick={() => setI((p) => (p + 1) % TESTIMONIALS.length)} data-testid="testimonial-next" className="w-12 h-12 border-2 border-white/20 flex items-center justify-center hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </section>
  );
};

const About = () => (
  <section className="py-24 lg:py-32 bg-[#F8FAFC]" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal>
        <div className="relative border-2 border-[#0B192C] hard-shadow-orange">
          <img src={IMG.about} alt="Logistics team handling freight in a warehouse" className="w-full h-[460px] object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <SectionLabel>Who We Are</SectionLabel>
        <h2 className="font-head font-black uppercase text-5xl md:text-6xl tracking-tighter leading-[0.9]">A founder-led freight broker</h2>
        <p className="text-[#475569] text-lg mt-6 leading-relaxed">
          Cargowyn was built on seven years inside the logistics industry — and one conviction: cross-border shippers deserve a broker who answers the phone, quotes honestly, and treats every load like it matters.
        </p>
        <p className="text-[#475569] text-lg mt-4 leading-relaxed">
          Our founder holds a <span className="text-[#0B192C] font-700">CIFFA certification</span> and specializes in the oversize and heavy-haul freight most brokers avoid. As we grow into ocean, air and China–Canada import services, that same standard of reliability comes with us.
        </p>
        <div className="flex flex-wrap gap-4 mt-8">
          <div className="flex items-center gap-3 border-2 border-[#0B192C]/10 px-5 py-3"><Award className="text-[#FF5A00]" /><span className="font-head font-700 uppercase tracking-wide text-sm">CIFFA Certified</span></div>
          <div className="flex items-center gap-3 border-2 border-[#0B192C]/10 px-5 py-3"><BadgeCheck className="text-[#94A3B8]" /><span className="font-head font-700 uppercase tracking-wide text-sm text-[#94A3B8]">FMCSA Authority — Pending</span></div>
        </div>
        <Link to="/about" data-testid="about-more-btn" className="inline-flex items-center gap-2 mt-8 font-head font-700 uppercase tracking-[0.15em] text-[#FF5A00] hover:gap-4 transition-all">
          Read Our Story <ArrowRight size={18} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Certifications = () => (
  <section className="py-16 bg-white border-y-2 border-[#0B192C]/5" data-testid="certs-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
      <p className="font-head uppercase tracking-[0.3em] text-[#94A3B8] text-sm font-700">Certifications & Affiliations</p>
      <div className="flex flex-wrap items-center gap-4">
        {[["CIFFA", "Canadian Int'l Freight Forwarders Assoc.", true], ["FMCSA", "US Broker Authority — Pending", false], ["Bonded", "Cross-Border Carrier Network", true]].map(([t, d, active], i) => (
          <div key={i} className={`flex items-center gap-3 border-2 px-5 py-4 ${active ? "border-[#0B192C]" : "border-[#0B192C]/10"}`} data-testid={`cert-${i}`}>
            <div className={`w-10 h-10 flex items-center justify-center ${active ? "bg-[#FF5A00]" : "bg-[#E2E8F0]"}`}>
              <ShieldCheck size={20} className={active ? "text-white" : "text-[#94A3B8]"} />
            </div>
            <div>
              <div className={`font-head font-800 uppercase ${active ? "text-[#0B192C]" : "text-[#94A3B8]"}`}>{t}</div>
              <div className="text-[#94A3B8] text-[11px]">{d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section className="relative py-24 lg:py-32 bg-[#FF5A00] overflow-hidden" data-testid="final-cta">
    <div className="absolute inset-0 noise opacity-[0.08]" />
    <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center relative">
      <h2 className="font-head font-black uppercase text-white text-5xl md:text-8xl tracking-tighter leading-[0.9]">Get a free quote</h2>
      <p className="text-white/90 text-lg md:text-xl mt-6 max-w-xl mx-auto">Tell us what you're moving and where. We'll come back with an honest, all-in rate — fast.</p>
      <Link to="/quote" data-testid="final-cta-btn" className="inline-flex items-center gap-3 mt-10 bg-[#0B192C] text-white font-head font-700 uppercase tracking-[0.15em] px-10 py-5 hover:bg-white hover:text-[#0B192C] transition-colors">
        Start Your Quote <ArrowRight size={20} />
      </Link>
    </div>
  </section>
);

export default function Home() {
  useSEO("Canada–US Freight Brokerage", "Cargowyn Logistics Group is an Ontario-based cross-border freight broker offering FTL, LTL, flatbed and specialized oversize & heavy-haul trucking between Canada and the US.");
  return (
    <div>
      <Hero />
      <StatBar />
      <Services />
      <Solutions />
      <Locations />
      <ValueProp />
      <Logos />
      <Marquee text="RELIABLE · TRANSPARENT · CROSS-BORDER" />
      <Differentiators />
      <Testimonials />
      <About />
      <Certifications />
      <FinalCTA />
    </div>
  );
}
